import React, { useContext } from 'react';
import { AppContext, InvoiceProductInfo } from '../../../AppContext';
import CSS from 'csstype';
import { Colors } from '../../../utils/theme';
import moment from 'moment';
import { BELGIUM, EU_COUNTRIES } from '../../../enums/CountryEnums';
import { AC_NAME, RB_NAME } from '../../../enums/MembershipEnums';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import epra_logo from '../../../assets/images/logos/epra_logo.svg';
import {
  CheckCircle,
  Email,
  HomeWork,
  LocationOn,
  Phone,
  Print,
  Receipt,
  Today
} from '@mui/icons-material';
import { formatDate } from '../../../utils/ui';
import { InvoiceStatus } from '../../../enums/InvoiceStatusEnums';
import { IContact, IInvoice, IPaymentDetails } from '../../../generated/graphql';

interface IProps {
  fullscreen?: boolean;
  cventData?: {
    contact: IContact;
    paymentDetails: IPaymentDetails;
    productInfo: InvoiceProductInfo;
  };
  dbContact: IContact;
}
const Invoice: React.FC<IProps> = ({ fullscreen, cventData, dbContact }: IProps) => {
  const { state } = useContext(AppContext);
  const { selectedInvoice } = state;
  const isCredit = selectedInvoice?.invoice_full?.is_credit;
  const isConfirmed = selectedInvoice?.status?.includes('confirmed');
  const fontSize = fullscreen ? 12 : 4;
  const labelImageSize = fullscreen ? 14 : 6;
  const invoicePaymentDetails = cventData?.paymentDetails ?? selectedInvoice?.paymentDetails;
  const productInfo = cventData?.productInfo ?? selectedInvoice?.productInfo;
  const membershipPeriodStartDate = selectedInvoice?.remarks;
  const currencyForProduct =
    productInfo?.product?.product_code.name.toLowerCase() == 'esg' ? 'GBP' : 'EUR';
  const parentInputStyle: CSS.Properties = {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    fontSize,
    padding: 0,
    maxHeight: '20%',
    minHeight: '20%'
  };
  const rightParentInputStyle: CSS.Properties = {
    ...parentInputStyle,
    maxHeight: '16%',
    minHeight: '16%'
  };

  const labelIconStyle: CSS.Properties = {
    marginRight: '4.7%',
    marginBottom: '1%',
    color: Colors.third,
    width: `${labelImageSize}px`,
    height: `${labelImageSize}px`
  };

  const headerStyle: CSS.Properties = {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    fontSize: fullscreen ? 18 : 6,
    color: Colors.third,
    fontWeight: 'bold',
    fontFamily: 'Overpass'
  };

  const labelStyle: CSS.Properties = {
    color: Colors.main,
    fontFamily: 'Overpass',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    fontSize,
    '&:disabled': Colors.main
  };

  const smallTableCell: CSS.Properties = {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    fontSize,
    padding: '6px 16px 6px 16px',
    color: Colors.main
  };

  const prefixAndFullName = (invoiceFull: IInvoice | null, invoiceContact: IContact | null) => {
    if (!invoiceFull) {
      if (invoiceContact?.prefix === null) {
        return `${invoiceContact?.fullName}`;
      }
      return `${invoiceContact?.prefix} ${invoiceContact?.fullName}`;
    }
    if (invoiceFull.details.prefix === null) {
      return `${invoiceFull.details.fullName}`;
    }
    return `${invoiceFull?.details.prefix} ${invoiceFull?.details.fullName}`;
  };

  const getContactAddress = () => {
    return cventData
      ? `${cventData?.contact.address?.street}, ${cventData?.contact?.address?.zip}, ${cventData?.contact.address?.city} , ${cventData?.contact?.address?.country.name} `
      : selectedInvoice?.invoice_full
      ? `${selectedInvoice.invoice_full.details.street + ', ' ?? ''} ${
          selectedInvoice.invoice_full.details.zip
            ? selectedInvoice.invoice_full.details.zip + ', '
            : ''
        } ${selectedInvoice.invoice_full.details.city + ', ' ?? ''} ${
          selectedInvoice.invoice_full.details.country ?? ''
        }`
      : `${selectedInvoice?.address?.country.name ?? ''}, ${
          selectedInvoice?.address?.street ?? ''
        }`;
  };

  const getDate = (input: 'created_date' | 'due_date') => {
    if (selectedInvoice?.invoice_full?.[input]) {
      return moment(parseInt(selectedInvoice?.invoice_full?.[input] as string)).format(
        'D MMM YYYY'
      );
    }
  };

  const getVatComment = () => {
    let result = '';
    const country = selectedInvoice?.invoice_full
      ? (selectedInvoice.invoice_full.details.country as string)
      : (selectedInvoice?.address?.country?.name as string);
    if (country) {
      if (productInfo?.product?.product_code?.name === RB_NAME) {
        if (country.toLocaleLowerCase() === BELGIUM) {
          result = '';
        } else if (EU_COUNTRIES.indexOf(country.toLocaleLowerCase()) > -1) {
          result =
            'Service located abroad - Article 44 of Directive 2006/112/EG - Reverse charge - Article 196 of Directive 2006/112/EG';
        } else {
          result = 'Service located abroad - Article 59(b) of Directive 2006/112/EG';
        }
      } else if (productInfo?.product?.product_code?.name === AC_NAME) {
        if (EU_COUNTRIES.indexOf(country.toLocaleLowerCase()) === -1) {
          result = 'Service located abroad - Article 59(b) of Directive 2006/112/EG';
        }
      }
    }

    return result;
  };

  const getBottomNotes = (input: string | null | undefined) => {
    if (input) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      let result = input.replaceAll('<font', '<span');
      result = result.replaceAll('<p', '<span');
      result = result.replaceAll('font-size', '');
      result = result.replaceAll('<br>', ' ');
      result = result.replaceAll('b>', 'p>');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return result.replaceAll('size=', '');
    }
    return '';
  };

  const getCompleteVat = (countryCode: string, vatNumber: string) => {
    let completeVat = '';
    if (countryCode) {
      if (vatNumber) {
        if (vatNumber.startsWith(countryCode)) {
          completeVat = vatNumber;
        } else {
          completeVat = `${countryCode}${vatNumber}`;
        }
      }
    } else {
      completeVat = vatNumber ? vatNumber : '';
    }
    return completeVat;
  };

  return (
    <Card style={{ border: 'none', boxShadow: 'none', height: '100%', borderRadius: 0 }}>
      <CardContent style={{ padding: 0, height: '100%' }}>
        <Grid
          container
          style={{
            padding: '5.42% 5.67% 6.78% 4.66%',
            height: '26.6%'
          }}
        >
          <Grid item xs={3} style={{ display: 'flex', alignItems: 'center' }}>
            <img
              style={{ width: fullscreen ? 163 : 75 }}
              src={epra_logo}
              alt="logo"
              data-testid="invoice-epra-logo"
            />
          </Grid>
          <Grid item xs={1}>
            <Divider
              orientation="vertical"
              data-testid="invoice-header-divider"
              style={{ marginLeft: 'auto', marginRight: 'auto' }}
            />
          </Grid>
          <Grid item xs={8}>
            <Grid container style={{ height: '100%' }}>
              <Grid
                item
                xs={7}
                style={{ paddingRight: '6%', lineHeight: fullscreen ? 'auto' : '15px' }}
              >
                <InputBase
                  id="invoice-contact-address"
                  data-testid="invoice-contact-address"
                  value={invoicePaymentDetails?.contact_address_name}
                  disabled
                  fullWidth
                  startAdornment={<HomeWork style={labelIconStyle} />}
                  inputProps={{
                    style: { ...labelStyle, fontWeight: 'bold' }
                  }}
                  multiline
                  style={parentInputStyle}
                />
                <InputBase
                  id="invoice-address"
                  data-testid="invoice-address"
                  value={invoicePaymentDetails?.contact_address}
                  disabled
                  fullWidth
                  startAdornment={<LocationOn style={labelIconStyle} />}
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={parentInputStyle}
                />
                <InputBase
                  id="invoice-contact-phone"
                  data-testid="invoice-contact-phone"
                  value={invoicePaymentDetails?.contact_phone}
                  disabled
                  fullWidth
                  startAdornment={<Phone style={labelIconStyle} />}
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={parentInputStyle}
                />
                <InputBase
                  id="invoice-contact-fax"
                  data-testid="invoice-contact-fax"
                  value={invoicePaymentDetails?.contact_fax}
                  disabled
                  fullWidth
                  startAdornment={<Print style={labelIconStyle} />}
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={parentInputStyle}
                />
                <InputBase
                  id="invoice-contact-vat"
                  data-testid="invoice-contact-vat"
                  value={`VAT: ${invoicePaymentDetails?.contact_vat}`}
                  disabled
                  fullWidth
                  startAdornment={<Receipt style={labelIconStyle} />}
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={parentInputStyle}
                />
              </Grid>
              <Grid item xs={5} style={{ lineHeight: fullscreen ? 'auto' : '9px' }}>
                <div
                  style={{
                    fontFamily: 'Overpass',
                    color: Colors.white,
                    fontWeight: 'bold',
                    backgroundColor: Colors.main,
                    fontSize,
                    minHeight: '15%',
                    maxHeight: '15%',
                    marginLeft: '-4.1%',
                    paddingLeft: '4.1%',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  BANK ACCOUNT
                </div>
                <InputBase
                  id="invoice-bank-affiliate"
                  data-testid="invoice-bank-affiliate"
                  value={invoicePaymentDetails?.bank_affiliate}
                  disabled
                  fullWidth
                  inputProps={{
                    style: { ...labelStyle, fontWeight: 'bold' }
                  }}
                  multiline
                  style={rightParentInputStyle}
                />
                <InputBase
                  id="invoice-bank-name"
                  data-testid="invoice-bank-name"
                  value={invoicePaymentDetails?.bank_name}
                  disabled
                  fullWidth
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={rightParentInputStyle}
                />
                <InputBase
                  id="invoice-bank-address"
                  data-testid="invoice-bank-address"
                  value={invoicePaymentDetails?.bank_address}
                  disabled
                  fullWidth
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={rightParentInputStyle}
                />
                <InputBase
                  id="invoice-bank-pic"
                  data-testid="invoice-bank-pic"
                  value={invoicePaymentDetails?.bank_bic}
                  disabled
                  startAdornment={
                    <span style={{ ...labelStyle, fontWeight: 'bold', marginRight: '3%' }}>
                      BIC:
                    </span>
                  }
                  fullWidth
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={rightParentInputStyle}
                />
                <InputBase
                  id="invoice-bank-iban"
                  data-testid="invoice-bank-iban"
                  value={invoicePaymentDetails?.bank_iban}
                  disabled
                  fullWidth
                  startAdornment={
                    <span style={{ ...labelStyle, fontWeight: 'bold', marginRight: '3%' }}>
                      IBAN:
                    </span>
                  }
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={rightParentInputStyle}
                />
                {/* </div> */}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container style={{ height: '4.65%' }}>
          <Grid
            item
            xs={5}
            style={{
              background: isCredit ? Colors.orange : Colors.third,
              paddingLeft: '4.66%',
              paddingTop: '0.3%',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <span
              id="invoice-preview-title"
              data-testid="invoice-preview-title"
              style={{
                color: Colors.white,
                fontWeight: 'bold',
                fontSize: fullscreen ? 22 : 9,
                fontFamily: 'Overpass'
              }}
            >
              {`${isCredit ? 'CREDIT' : 'INVOICE'} ${
                isConfirmed ? selectedInvoice?.invoice_full?.number : ''
              }`}
            </span>
          </Grid>
          <Grid
            item
            xs={7}
            style={{ marginTop: 'auto', marginBottom: 'auto', paddingRight: '5.13%' }}
          >
            <Divider style={{ background: isCredit ? Colors.orange : Colors.main }} />
          </Grid>
        </Grid>
        <Grid
          container
          style={{
            padding: '0 5.67% 0 4.66%',
            height: fullscreen ? '19.9%' : '14.9%',
            lineHeight: fullscreen ? '1.5' : '10px'
          }}
        >
          <Grid
            item
            xs={12}
            style={{
              height: '17%',
              color: Colors.orange,
              fontSize: fullscreen ? 14 : 4,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {isCredit && `/REF: INVOICE  ${selectedInvoice?.invoice_full?.debit_invoice_nr ?? ''}/`}
          </Grid>
          <Grid container style={{ height: '83%' }}>
            <Grid item xs={4} style={{ paddingRight: '6%' }}>
              <InputBase
                value="To:"
                disabled
                fullWidth
                inputProps={{
                  style: {
                    fontFamily: 'Overpass',
                    fontSize
                  }
                }}
                multiline
                style={{ padding: 0, fontSize }}
              />
              <InputBase
                id="invoice-contact-fullname"
                data-testid="invoice-contact-fullname"
                value={
                  prefixAndFullName(
                    selectedInvoice?.invoice_full || null,
                    selectedInvoice?.contact || null
                  )
                  // selectedInvoice?.invoice_full?.details?.prefix
                  //   ? `${
                  //       selectedInvoice?.invoice_full?.details?.prefix +
                  //       ' ' +
                  //       selectedInvoice?.contact?.fullName
                  //     }`
                  //   : `${dbContact?.prefix} ${selectedInvoice?.contact?.fullName}`
                  // `${
                  //   dbContact?.prefix + ' ' + selectedInvoice?.contact?.fullName
                  // }`
                }
                disabled
                fullWidth
                multiline
                style={headerStyle}
              />
              <InputBase
                id="invoice-contact-email"
                data-testid="invoice-contact-email"
                value={selectedInvoice?.contact?.email}
                disabled
                fullWidth
                inputProps={{ style: labelStyle }}
                multiline
                style={{ padding: 0, fontSize }}
                startAdornment={<Email style={{ ...labelIconStyle, marginBottom: 0 }} />}
              />
              <InputBase
                id="invoice-contact-title"
                data-testid="invoice-contact-title"
                value={
                  selectedInvoice?.invoice_full?.details?.title_external ||
                  selectedInvoice?.contact?.title_external ||
                  dbContact?.title_external ||
                  ''
                }
                disabled
                fullWidth
                inputProps={{ style: labelStyle }}
                multiline
                style={{ padding: 0, fontSize }}
              />
            </Grid>
            <Grid item xs={4} style={{ paddingRight: '6%' }}>
              <InputBase
                value="Company:"
                disabled
                fullWidth
                inputProps={{
                  style: {
                    fontFamily: 'Overpass',
                    fontSize
                  }
                }}
                multiline
                style={{ padding: 0, fontSize }}
              />
              <InputBase
                id="invoice-company-name"
                data-testid="invoice-company-name"
                value={selectedInvoice?.company?.name}
                disabled
                fullWidth
                multiline
                style={headerStyle}
              />
              <InputBase
                id="invoice-contact-address"
                data-testid="invoice-contact-address"
                value={getContactAddress()}
                disabled
                fullWidth
                inputProps={{ style: labelStyle }}
                multiline
                style={{ padding: 0, fontSize }}
              />
              <InputBase
                id="invoice-address-vat"
                data-testid="invoice-address-vat"
                value={`VAT: ${
                  !selectedInvoice?.invoice_full
                    ? selectedInvoice?.company?.main_address?.complete_vat_number
                    : getCompleteVat(
                        selectedInvoice?.invoice_full?.details.vat_countrycode as string,
                        selectedInvoice?.invoice_full?.details.company_address_vat as string
                      ) ??
                      cventData?.paymentDetails?.contact_vat ??
                      ''
                }`}
                disabled
                fullWidth
                inputProps={{ style: labelStyle }}
                multiline
                style={{ padding: 0, fontSize, marginTop: '5%' }}
              />
            </Grid>
            <Grid item xs={4}>
              <InputBase
                value=""
                disabled
                fullWidth
                inputProps={{
                  style: {
                    fontFamily: 'Overpass',
                    fontSize
                  }
                }}
                multiline
                style={{ padding: 0, fontSize }}
              />
              <InputBase
                id="invoice-confirmed-circle"
                data-testid="invoice-confirmed-circle"
                value={isConfirmed ? 'confirmed' : ''}
                disabled
                fullWidth
                startAdornment={
                  isConfirmed ? (
                    <CheckCircle
                      className="invoice-confirmed-circle-icon"
                      data-testid="invoice-confirmed-circle-icon"
                      style={{ ...labelIconStyle, color: Colors.green }}
                    />
                  ) : null
                }
                multiline
                style={{ ...headerStyle, color: Colors.green }}
              />
              <InputBase
                id="invoice-created-date"
                data-testid="invoice-created-date"
                value={getDate('created_date')}
                disabled
                fullWidth
                startAdornment={
                  <InputBase
                    value="Invoice date:"
                    disabled
                    startAdornment={<Today style={{ ...labelIconStyle, marginRight: '8%' }} />}
                    inputProps={{ style: { ...labelStyle, color: Colors.third, padding: 0 } }}
                    style={{ padding: 0, width: '100%' }}
                  />
                }
                inputProps={{ style: { ...labelStyle, fontWeight: 'bold', marginLeft: '-14%' } }}
                multiline
                style={{ padding: 0, fontSize, marginTop: '2.2%' }}
              />
              <InputBase
                id="invoice-due-date"
                data-testid="invoice-due-date"
                value={getDate('due_date')}
                disabled
                fullWidth
                startAdornment={
                  <InputBase
                    value="Invoice due:"
                    disabled
                    startAdornment={<Today style={{ ...labelIconStyle, marginRight: '8%' }} />}
                    inputProps={{ style: { ...labelStyle, color: Colors.third, padding: 0 } }}
                    style={{ padding: 0, width: '100%' }}
                  />
                }
                inputProps={{ style: { ...labelStyle, fontWeight: 'bold', marginLeft: '-14%' } }}
                multiline
                style={{ padding: 0, fontSize, marginTop: '2.4%' }}
              />
              {selectedInvoice?.po_nr && (
                <InputBase
                  id="invoice-po-nr"
                  data-testid="invoice-po-nr"
                  value={`PO: ${selectedInvoice?.po_nr || ''}`}
                  disabled
                  fullWidth
                  inputProps={{ style: labelStyle }}
                  multiline
                  style={{ padding: 0, fontSize, marginTop: '5%' }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          id="invoice-additional-details"
          style={{
            padding: '0 5.67% 0 4.66%',
            height: fullscreen ? '26.85%' : '31.8%'
          }}
        >
          <Grid item xs={12} style={{ height: '100%' }}>
            <Table aria-label="spanning table">
              <TableHead>
                {membershipPeriodStartDate && (
                  <TableRow>
                    <TableCell
                      style={{
                        ...smallTableCell,
                        fontWeight: 'bold',
                        paddingLeft: 0,
                        paddingBottom: 0,
                        borderBottom: 'none',
                        width: '20%',
                        lineHeight: fullscreen ? 'auto' : '6px',
                        fontSize: fullscreen ? '10px' : '3px',
                        color: Colors.main
                      }}
                    >
                      {membershipPeriodStartDate || ''}
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      ...smallTableCell,
                      fontWeight: 'bold',
                      paddingLeft: 0,
                      borderBottom: `1px solid ${Colors.main}`,
                      width: '60%',
                      lineHeight: fullscreen ? 'auto' : '6px',
                      padding: '0'
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    colSpan={3}
                    align="right"
                    style={{
                      ...smallTableCell,
                      fontWeight: 'bold',
                      borderBottom: `1px solid ${Colors.main}`,
                      width: '40%',
                      lineHeight: fullscreen ? 'auto' : '6px'
                    }}
                  >
                    Price
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    data-testid="invoice-product-name"
                    style={{
                      ...smallTableCell,
                      paddingLeft: 0,
                      borderBottom: `1px solid ${Colors.main}`
                    }}
                  >
                    {productInfo?.product?.name}
                  </TableCell>
                  <TableCell
                    data-testid="invoice-price"
                    colSpan={3}
                    align="right"
                    style={{
                      ...smallTableCell,
                      borderBottom: `1px solid ${Colors.main}`
                    }}
                  >
                    {productInfo?.product?.price.toLocaleString(undefined, {
                      minimumFractionDigits: 2
                    }) +
                      ' ' +
                      currencyForProduct}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell rowSpan={3} style={{ border: 'none' }} />
                  <TableCell
                    data-testid="invoice-discount-percent"
                    colSpan={2}
                    style={{
                      ...smallTableCell,
                      borderBottom: `1px solid ${Colors.third}`,
                      paddingLeft: 0
                    }}
                  >
                    Discount({productInfo?.discountPercent}%)
                  </TableCell>
                  <TableCell
                    data-testid="invoice-discount"
                    align="right"
                    style={{
                      ...smallTableCell,
                      borderBottom: `1px solid ${Colors.third}`,
                      paddingLeft: 0
                    }}
                  >
                    {productInfo?.discount.toFixed(2) + ' ' + currencyForProduct}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    data-testid="invoice-vat-percent"
                    colSpan={2}
                    style={{
                      ...smallTableCell,
                      borderBottom: `1px solid ${Colors.third}`,
                      paddingLeft: 0
                    }}
                  >
                    VAT({productInfo?.vatPercent}%)
                  </TableCell>
                  <TableCell
                    data-testid="invoice-vat"
                    align="right"
                    style={{
                      ...smallTableCell,
                      borderBottom: `1px solid ${Colors.third}`,
                      paddingLeft: 0
                    }}
                  >
                    {productInfo?.vat.toFixed(2) + ' ' + currencyForProduct}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    style={{
                      ...smallTableCell,
                      fontWeight: 'bold',
                      borderBottom: 'none',
                      paddingLeft: 0
                    }}
                  >
                    TOTAL:
                  </TableCell>
                  <TableCell
                    data-testid="invoice-total"
                    align="right"
                    style={{
                      ...smallTableCell,
                      fontWeight: 'bold',
                      borderBottom: 'none',
                      paddingLeft: 0
                    }}
                  >
                    {productInfo?.total.toLocaleString(undefined, { minimumFractionDigits: 2 }) +
                      ' ' +
                      currencyForProduct}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <div
              style={{
                color: Colors.main,
                fontFamily: 'Overpass',
                fontSize: fullscreen ? '9px' : '6px'
              }}
            >
              {getVatComment()}
            </div>
            {selectedInvoice?.notes && (
              <Box sx={{ labelStyle, fontSize: fullscreen ? '9px' : '6px' }}>
                {selectedInvoice?.notes}
              </Box>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          style={{
            padding: '0% 5.67% 0 4.66%',
            paddingBottom: '0',
            height: '24.1%',
            backgroundColor: Colors.third,
            alignContent: 'flex-start'
          }}
        >
          <Grid
            item
            xs={12}
            id="invoice-bottom-notes"
            data-testid="invoice-bottom-notes"
            style={{
              color: Colors.white,
              fontSize: fullscreen ? '12px' : '5px',
              minHeight: '50%'
            }}
            dangerouslySetInnerHTML={{
              __html: getBottomNotes(productInfo?.product?.bottom_notes)
            }}
          ></Grid>
          {!productInfo?.product?.name?.toLowerCase()?.includes('invoice credit card') ? (
            <Grid
              item
              xs={12}
              style={{
                color: Colors.white,
                fontSize,
                fontStyle: 'italic',
                borderTop: '1px solid #B4D5F9',
                height: '15%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              We kindly request you to pay the above total amount in{' '}
              {productInfo?.product?.product_code.name.toLowerCase() === 'esg' ? 'GBP' : 'Euros'}{' '}
              ("charges to the payer")
            </Grid>
          ) : (
            ''
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default Invoice;
